import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Percent, Save, Settings } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useVat } from '../../contexts/VatContext';

const VatSettings = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { vatPercentage, updateVatPercentage, fetchVatSettings } = useVat();
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [localVatPercentage, setLocalVatPercentage] = useState(vatPercentage);

  useEffect(() => {
    fetchVatSettings();
  }, [fetchVatSettings]);

  useEffect(() => {
    setLocalVatPercentage(vatPercentage);
  }, [vatPercentage]);



  const handleSave = async () => {
    if (localVatPercentage < 0 || localVatPercentage > 100) {
      setMessage(t('vatSettings.vatPercentageMustBeBetween'));
      return;
    }

    setIsLoading(true);
    setMessage('');

    try {
      const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${API_BASE_URL}/vat-settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ vat_percentage: parseFloat(localVatPercentage) })
      });

      if (response.ok) {
        updateVatPercentage(parseFloat(localVatPercentage));
        setMessage(t('vatSettings.vatSettingsUpdatedSuccessfully'));
        setIsEditing(false);
        setTimeout(() => setMessage(''), 3000);
      } else {
        const errorData = await response.json();
        setMessage(errorData.error || t('vatSettings.failedToUpdateVatSettings'));
      }
    } catch (error) {
      console.error('Error updating VAT settings:', error);
      setMessage(t('vatSettings.failedToUpdateVatSettings'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setLocalVatPercentage(vatPercentage); // Reset to current value
    setIsEditing(false);
    setMessage('');
  };

  return (
    <div>
      <div className={`flex items-center mb-4 ${i18n.language === 'ar' ? 'space-x-reverse space-x-2' : 'space-x-2'}`}>
        <Settings className="h-5 w-5 text-muted-foreground" />
        <h3 className="text-lg font-medium text-card-foreground">{t('vatSettings.title')}</h3>
      </div>
      
      <div className="space-y-3">
        <div>
          <label className="block text-xs font-medium text-card-foreground mb-1">
            {t('vatSettings.vatPercentage')}
          </label>
          <div className={`flex items-center ${i18n.language === 'ar' ? 'space-x-reverse space-x-2' : 'space-x-2'}`}>
            <div className="relative flex-1">
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={localVatPercentage}
                onChange={(e) => setLocalVatPercentage(e.target.value)}
                disabled={!isEditing}
                className={`w-full px-3 py-2 text-sm border rounded-md bg-background text-foreground ${
                  isEditing 
                    ? 'border-border focus:border-primary focus:ring-1 focus:ring-primary' 
                    : 'border-border bg-muted cursor-not-allowed'
                }`}
                placeholder="15.00"
              />
              <div className={`absolute inset-y-0 flex items-center pointer-events-none ${
                i18n.language === 'ar' ? 'left-0 pl-3' : 'right-0 pr-3'
              }`}>
                <Percent className="h-4 w-4 text-muted-foreground" />
              </div>
            </div>
            
            {!isEditing ? (
              <button
                onClick={() => setIsEditing(true)}
                className="px-3 py-2 text-xs font-medium text-card-foreground bg-background border border-border rounded-md hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
{t('vatSettings.edit')}
              </button>
            ) : (
              <div className={`flex ${i18n.language === 'ar' ? 'space-x-reverse space-x-1' : 'space-x-1'}`}>
                <button
                  onClick={handleSave}
                  disabled={isLoading}
                  className="px-3 py-2 text-xs font-medium text-primary-foreground bg-primary border border-transparent rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-primary-foreground mr-1"></div>
                      {t('vatSettings.save')}
                    </div>
                  ) : (
                    <div className="flex items-center">
                      <Save className="h-3 w-3 mr-1" />
                      {t('vatSettings.save')}
                    </div>
                  )}
                </button>
                <button
                  onClick={handleCancel}
                  className="px-3 py-2 text-xs font-medium text-card-foreground bg-background border border-border rounded-md hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring"
                >
{t('vatSettings.cancel')}
                </button>
              </div>
            )}
          </div>
        </div>

        {message && (
          <div className={`text-xs p-2 rounded-md ${
            message.includes('successfully') 
              ? 'bg-chart-3/10 text-chart-3 border border-chart-3/20' 
              : 'bg-destructive/10 text-destructive border border-destructive/20'
          }`}>
            {message}
          </div>
        )}

        <div className="text-xs text-muted-foreground">
          <p>• {t('vatSettings.changingVatPercentageWillRecalculate')}</p>
          <p>• {t('vatSettings.defaultVatRate')}</p>
          <p>• {t('vatSettings.pricesDisplayedWithVatIncluded')}</p>
        </div>
      </div>
    </div>
  );
};

export default VatSettings;
